import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Button, Tabs, Tab
} from '@mui/material';
import {
  Save as SaveIcon, Preview as PreviewIcon, Settings as SettingsIcon, 
  ArrowBack as ArrowBackIcon, Description, Psychology as AiIcon,
  FileUpload as ImportIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const FormHeader = ({ 
  formTitle, 
  activeTab, 
  setActiveTab, 
  onPreviewOpen, 
  onSaveForm, 
  onAIFormOpen,
  onDocumentImport,
  isDirty,
  isSaving = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <AppBar position="static" color="default" elevation={0} sx={{ 
      mb: 3,
      borderRadius: 2,
      mx: 3,
      background: 'linear-gradient(to right, #ffffff, #f8fafc)',
      border: '1px solid #e2e8f0',
      '& .MuiToolbar-root': {
        minHeight: '64px !important',
      }
    }}>
      <Toolbar>
        <IconButton 
          edge="start" 
          onClick={() => navigate('/')}
          sx={{ 
            mr: 2,
            color: '#64748b',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              color: '#1976d2'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ 
          ml: 2, 
          flexGrow: 1,
          fontWeight: 600,
          color: '#1e293b',
          letterSpacing: '-0.5px'
        }}>
          {formTitle || t('new_form')}
        </Typography>
        
        <Button
          startIcon={<ImportIcon />}
          variant="outlined"
          onClick={onDocumentImport}
          sx={{ 
            mr: 2,
            color: '#059669',
            borderColor: '#10b981',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            background: 'linear-gradient(45deg, rgba(5, 150, 105, 0.05) 30%, rgba(16, 185, 129, 0.05) 90%)',
            '&:hover': {
              borderColor: '#059669',
              color: '#059669',
              backgroundColor: 'rgba(5, 150, 105, 0.08)',
              boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)'
            }
          }}
        >
          📄 İçe Aktar
        </Button>
        
        {/* AI Button - Temporarily Hidden */}
        {false && (
        <Button
          startIcon={<AiIcon />}
          variant="outlined"
          onClick={onAIFormOpen}
          sx={{ 
            mr: 2,
            color: '#9333ea',
            borderColor: '#e879f9',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            background: 'linear-gradient(45deg, rgba(147, 51, 234, 0.05) 30%, rgba(232, 121, 249, 0.05) 90%)',
            '&:hover': {
              borderColor: '#9333ea',
              color: '#9333ea',
              backgroundColor: 'rgba(147, 51, 234, 0.08)',
              boxShadow: '0 2px 4px rgba(147, 51, 234, 0.2)'
            }
          }}
        >
          🤖 AI Form
        </Button>
        )}
        
        <Button
          startIcon={<PreviewIcon />}
          variant="outlined"
          onClick={onPreviewOpen}
          sx={{ 
            mr: 2,
            color: '#64748b',
            borderColor: '#e2e8f0',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            '&:hover': {
              borderColor: '#1976d2',
              color: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.1)'
            }
          }}
        >
          {t('preview')}
        </Button>
        <Button
          startIcon={<SaveIcon />}
          variant="contained"
          onClick={onSaveForm}
          disabled={!isDirty || isSaving}
          sx={{ 
            background: isSaving 
              ? 'linear-gradient(45deg, #94a3b8 30%, #cbd5e1 90%)'
              : 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            boxShadow: isSaving 
              ? 'none'
              : '0 2px 4px rgba(25, 118, 210, 0.2)',
            '&:hover': {
              background: isSaving 
                ? 'linear-gradient(45deg, #94a3b8 30%, #cbd5e1 90%)'
                : 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              boxShadow: isSaving 
                ? 'none'
                : '0 4px 8px rgba(25, 118, 210, 0.3)'
            },
            '&.Mui-disabled': {
              background: '#e2e8f0',
              color: '#94a3b8'
            }
          }}
        >
          {isSaving ? t('saving_form') || 'Kaydediliyor...' : t('save')}
        </Button>
      </Toolbar>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          setActiveTab(newValue);
        }}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        sx={{ 
          borderBottom: '1px solid #e2e8f0',
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: '#1976d2',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 48,
            padding: '12px 16px',
            color: '#64748b',
            '&:hover': {
              color: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
            '&.Mui-selected': {
              color: '#1976d2',
              fontWeight: 600,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
            '& .MuiSvgIcon-root': {
              marginRight: 1,
              fontSize: '1.25rem',
            }
          }
        }}
      >
        <Tab 
          icon={<Description />} 
          label={t('edit')} 
          iconPosition="start"
        />
        <Tab 
          icon={<SettingsIcon />} 
          label={t('settings')} 
          iconPosition="start"
        />
      </Tabs>
    </AppBar>
  );
};

export default FormHeader;
